import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type {
  TodoBootstrapPayload,
  TodoList,
  TodoProject,
  TodoTag,
  TodoTask,
  TodoTaskEvent,
} from '@/types/todo';
import { normalizeTodoTask } from './todoTaskUtils';

interface TodoStoreState {
  initialized: boolean;
  loading: boolean;
  bootstrapFailed: boolean;
  lists: Record<string, TodoList>;
  listOrder: string[];
  projects: Record<string, TodoProject>;
  tags: Record<string, TodoTag>;
  tasks: Record<string, TodoTask>;
  taskOrder: string[];
  taskEvents: TodoTaskEvent[];
  hydrate: (payload: TodoBootstrapPayload) => void;
  setLoading: (loading: boolean) => void;
  setBootstrapFailed: (failed: boolean) => void;
  upsertList: (list: TodoList) => void;
  removeList: (listId: string) => void;
  upsertProject: (project: TodoProject) => void;
  removeProject: (projectId: string) => void;
  upsertTag: (tag: TodoTag) => void;
  removeTag: (tagId: string) => void;
  upsertTask: (task: TodoTask) => void;
  removeTask: (taskId: string) => void;
  setTaskEvents: (events: TodoTaskEvent[]) => void;
  appendTaskEvent: (event: TodoTaskEvent) => void;
  reset: () => void;
}

const sortLists = (lists: TodoList[]) =>
  lists
    .slice()
    .sort((a, b) => {
      if (a.isInbox && !b.isInbox) return -1;
      if (!a.isInbox && b.isInbox) return 1;
      if (a.sortKey === b.sortKey) {
        return a.name.localeCompare(b.name);
      }
      return a.sortKey - b.sortKey;
    })
    .map((list) => list.id);

const sortTasksByPosition = (tasks: TodoTask[]) =>
  tasks
    .slice()
    .sort((a, b) => {
      const aPos = Number(a.position ?? 0);
      const bPos = Number(b.position ?? 0);
      if (aPos === bPos) {
        return (a.updatedAt ?? '').localeCompare(b.updatedAt ?? '');
      }
      return aPos - bPos;
    })
    .map((task) => task.id);

// View payload functions removed - views are now computed from tasks store

export const useTodoStore = create<TodoStoreState>()(
  conditionalDevtools(
    persist(
      (set) => ({
        initialized: false,
        loading: false,
        bootstrapFailed: false,
        lists: {},
        listOrder: [],
        projects: {},
        tags: {},
        tasks: {},
        taskOrder: [],
        taskEvents: [],

        hydrate: (payload) => {
          const listsById = payload.lists.reduce<Record<string, TodoList>>((acc, list) => {
            acc[list.id] = list;
            return acc;
          }, {});
          const projectsById = payload.projects.reduce<Record<string, TodoProject>>((acc, project) => {
            acc[project.id] = project;
            return acc;
          }, {});
          const tagsById = payload.tags.reduce<Record<string, TodoTag>>((acc, tag) => {
            acc[tag.id] = tag;
            return acc;
          }, {});
          const tasksById = payload.tasks.reduce<Record<string, TodoTask>>((acc, task) => {
            const normalized = normalizeTodoTask(task);
            acc[normalized.id] = normalized;
            return acc;
          }, {});
          set({
            initialized: true,
            loading: false,
            bootstrapFailed: false,
            lists: listsById,
            listOrder: sortLists(payload.lists),
            projects: projectsById,
            tags: tagsById,
            tasks: tasksById,
            taskOrder: sortTasksByPosition(payload.tasks),
            taskEvents: payload.events ?? [],
          });
        },

        setLoading: (loading) => set({ loading }),

        setBootstrapFailed: (failed) => set({ bootstrapFailed: failed }),

        upsertList: (list) =>
          set((state) => {
            const lists = { ...state.lists, [list.id]: list };
            const order = state.listOrder.includes(list.id)
              ? state.listOrder.map((id) => lists[id]).filter(Boolean)
              : [...state.listOrder.map((id) => lists[id]).filter(Boolean), list];
            return {
              lists,
              listOrder: sortLists(order as TodoList[]),
            };
          }),

        removeList: (listId) =>
          set((state) => {
            const next = { ...state.lists };
            delete next[listId];
            return {
              lists: next,
              listOrder: state.listOrder.filter((id) => id !== listId),
            };
          }),

        upsertProject: (project) =>
          set((state) => ({
            projects: { ...state.projects, [project.id]: project },
          })),

        removeProject: (projectId) =>
          set((state) => {
            const next = { ...state.projects };
            delete next[projectId];
            return { projects: next };
          }),

        upsertTag: (tag) =>
          set((state) => ({
            tags: { ...state.tags, [tag.id]: tag },
          })),

        removeTag: (tagId) =>
          set((state) => {
            const next = { ...state.tags };
            delete next[tagId];
            return { tags: next };
          }),

        upsertTask: (task) =>
          set((state) => {
            const normalized = normalizeTodoTask(task);
            const nextTask = { ...(state.tasks[normalized.id] ?? {}), ...normalized };
            const tasks = { ...state.tasks, [normalized.id]: nextTask };
            const orderSet = new Set(state.taskOrder);
            orderSet.add(normalized.id);
            const sorted = sortTasksByPosition(Array.from(orderSet).map((id) => tasks[id]).filter(Boolean));
            return { tasks, taskOrder: sorted };
          }),

        removeTask: (taskId) =>
          set((state) => {
            if (!state.tasks[taskId]) {
              return state;
            }
            const tasks = { ...state.tasks };
            delete tasks[taskId];
            const taskOrder = state.taskOrder.filter((id) => id !== taskId);
            return { tasks, taskOrder };
          }),

        setTaskEvents: (events) => set({ taskEvents: events }),

        appendTaskEvent: (event) =>
          set((state) => ({
            taskEvents: [event, ...state.taskEvents].slice(0, 500),
          })),


        reset: () =>
          set({
            initialized: false,
            loading: false,
            bootstrapFailed: false,
            lists: {},
            listOrder: [],
            projects: {},
            tags: {},
            tasks: {},
            taskOrder: [],
            taskEvents: [],
          }),
      }),
      {
        name: 'todo-storage',
        version: 2,
        partialize: (state) => ({
          lists: state.lists,
          listOrder: state.listOrder,
          projects: state.projects,
          tags: state.tags,
        }),
      }
    ),
    { name: 'TodoStore' }
  )
);
